import React, { useCallback } from 'react'
import { Form, OverlayTrigger, Tooltip as BootstrapTooltip } from 'react-bootstrap'

import { useAccount } from '../../../common/useAccount'

import { useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

import { ZapierPromoBanner } from '../../../components/BusinessEvent/ZapierPromoBanner/ZapierPromoBanner'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'

import { DELTA_TRIAL_FEATURES } from '../../Plan/DeltaTrial/DeltaTrialFeatures'
import { Upsell } from '../../Plan/DeltaTrial/Upsell/Upsell'
import { getEnabledBusinessEventExport, getIntegrationDisplayName } from '../../../common/integrations'
import { IntegrationIcon } from '../../../components/IntegrationIcon'

import './BusinessEventSettings.scss'
import { useSaveBusinessEventExportWorkflow } from '../../Workflows/hooks'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

export const BusinessEventSettings = () => {
  const baseUrl = import.meta.env.REACT_APP_INTEGRATION_REDIRECT_URL
  const accountId = useAppSelector(selectAccountId)

  const { hasQuota } = useZocksQuota()
  const { trackingClient } = useTracking()

  const businessEventsEnabled = hasQuota('ai.businessEvent.value')

  const { account } = useAccount({ refetchOnMountOrArgChange: true })
  const [updateAccount, { isLoading: isAccountUpdateLoading }] = useUpdateAccountMutation()

  const { saveBusinessEventExportWorkflow } = useSaveBusinessEventExportWorkflow()

  const enabledBusinessEventExportServices = getEnabledBusinessEventExport()

  const businessEventExportEnabled = account?.effectiveConfig.businessEvent || account?.effectiveConfig.emailBusinessEvent

  const handleBusinessEventChange = useCallback(async () => {
    if (!account) {
      return
    }
    const enabled = !account.effectiveConfig.businessEvent
    const shouldDisableExport = !enabled && !account.effectiveConfig.emailBusinessEvent
    if (shouldDisableExport) {
      await saveBusinessEventExportWorkflow({ connectors: [] })
    }
    await updateAccount({
      accountId,
      payload: {
        config: {
          ...account.config,
          businessEvent: enabled,
          ...(shouldDisableExport ? { businessEventExport: false } : {}),
        },
      },
    }).unwrap()
    if (enabled) {
      trackingClient.track({
        eventName: TrackEvent.CLIENT_SIGNALS_ENABLED,
        eventDetails: { accountId },
      })
    }
  }, [account, accountId, updateAccount, trackingClient, saveBusinessEventExportWorkflow])

  const handleEmailBusinessEventChange = useCallback(async () => {
    if (!account) {
      return
    }
    const enabled = !account.effectiveConfig.emailBusinessEvent
    const shouldDisableExport = !enabled && !account.effectiveConfig.businessEvent
    if (shouldDisableExport) {
      await saveBusinessEventExportWorkflow({ connectors: [] })
    }
    await updateAccount({
      accountId,
      payload: {
        config: {
          ...account.config,
          emailBusinessEvent: enabled,
          ...(shouldDisableExport ? { businessEventExport: false } : {}),
        },
      },
    }).unwrap()
    if (enabled) {
      trackingClient.track({
        eventName: TrackEvent.CLIENT_SIGNALS_EMAIL_ENABLED,
        eventDetails: { accountId },
      })
    }
  }, [account, accountId, updateAccount, trackingClient, saveBusinessEventExportWorkflow])

  const handleBusinessEventExportChange = useCallback(async () => {
    if (!account || !businessEventExportEnabled) {
      return
    }
    const enabled = !account.effectiveConfig.businessEventExport
    await saveBusinessEventExportWorkflow({
      connectors: enabled ? enabledBusinessEventExportServices : [],
    })
    await updateAccount({
      accountId,
      payload: { config: { ...account.config, businessEventExport: enabled } },
    }).unwrap()
    if (enabled) {
      trackingClient.track({
        eventName: TrackEvent.CLIENT_SIGNAL_EXPORT_ENABLED,
        eventDetails: { accountId },
      })
    }
  }, [account, accountId, updateAccount, trackingClient, saveBusinessEventExportWorkflow, enabledBusinessEventExportServices, businessEventExportEnabled])

  if (!account) {
    return null
  }

  return (
    <div className='d-flex flex-column zocks-gap-2.5'>
      <h4>Client Signals</h4>
      {!businessEventsEnabled ? (
        <Upsell feature={DELTA_TRIAL_FEATURES['businessEvents']} />
      ) : (
        <>
          <div className='d-flex zocks-p-1.25 gap-3 align-items-center zocks-bg-color-primary-light zocks-border-0.5'>
            <i className='fs-5 bi bi-info-square zocks-text-primary' />
            <p className='d-flex flex-column paragraph-medium m-0'>
              <span className='zocks-text-gray-900'>
                Your AI Assistant detects important signals from meetings and emails in your account - such as life events and referrals -, highlights them in
                meeting notes and email threads and sends them to Zapier to trigger your workflows.
              </span>
              <a
                className='zocks-text-primary text-decoration-underline'
                href='https://help.zocks.io/en/articles/13421694-zocks-client-signals'
                target='_blank'
                rel='noopener noreferrer'
              >
                Learn more
              </a>
            </p>
          </div>
          <div className='d-flex gap-3'>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              missingScopeAction={MissingScopeAction.REPLACE}
              replaceElement={
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement='top'
                  overlay={<BootstrapTooltip className='zocks-text-gray-500'>Your account admin can edit this setting.</BootstrapTooltip>}
                >
                  <Form.Switch disabled={true} checked={account.effectiveConfig.businessEvent} />
                </OverlayTrigger>
              }
            >
              <Form.Switch onChange={handleBusinessEventChange} disabled={isAccountUpdateLoading} checked={account.effectiveConfig.businessEvent} />
            </ScopedBlock>
            <div className='d-flex flex-column gap-1'>
              <span className='paragraph-medium'>Enable detecting Client Signals</span>
              <span className='paragraph-small zocks-text-gray-700'>This affects all users in your account.</span>
            </div>
          </div>
          <div className='d-flex gap-3'>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              missingScopeAction={MissingScopeAction.REPLACE}
              replaceElement={
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement='top'
                  overlay={<BootstrapTooltip className='zocks-text-gray-500'>Your account admin can edit this setting.</BootstrapTooltip>}
                >
                  <Form.Switch disabled={true} checked={account.effectiveConfig.emailBusinessEvent} />
                </OverlayTrigger>
              }
            >
              <Form.Switch onChange={handleEmailBusinessEventChange} disabled={isAccountUpdateLoading} checked={account.effectiveConfig.emailBusinessEvent} />
            </ScopedBlock>
            <div className='d-flex flex-column gap-1'>
              <span className='paragraph-medium'>Enable detecting Client Signals for emails</span>
              <span className='paragraph-small zocks-text-gray-700'>This affects all users in your account.</span>
            </div>
          </div>
          <div className='d-flex flex-column zocks-gap-2.5'>
            <div className='d-flex flex-column gap-2'>
              <div className='lead-small zocks-text-gray-900'>Send to Zapier</div>
              <div className='d-flex flex-column gap-4'>
                <div className='d-flex zocks-p-1.25 gap-3 align-items-center zocks-bg-color-primary-light zocks-border-0.5'>
                  <i className='fs-5 bi bi-info-square zocks-text-primary' />
                  <p className='d-flex flex-column paragraph-medium m-0'>
                    <span className='zocks-text-gray-900'>
                      Connect Zapier to get the most out of Client Signals. Build automated workflows that trigger the moment a signal is detected – so every
                      life event and referral leads to the right next step, without any manual work.
                    </span>
                    <a
                      className='zocks-text-primary text-decoration-underline'
                      href='https://help.zocks.io/en/articles/13428173-zapier-integration-with-zocks'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Learn more
                    </a>
                  </p>
                </div>
                <ZapierPromoBanner orientation='horizontal' variant='ghost' />
              </div>
            </div>
            <div className='d-flex flex-column gap-2'>
              <div className='lead-small zocks-text-gray-900'>Export to CRM</div>
              <div className='d-flex flex-column zocks-gap-2'>
                <div className='d-flex zocks-p-1.25 gap-3 align-items-center zocks-bg-color-primary-light zocks-border-0.5'>
                  <i className='fs-5 bi bi-info-square zocks-text-primary' />
                  <p className='d-flex flex-column paragraph-medium m-0'>
                    <span className='zocks-text-gray-900'>
                      Send Client Signals to your CRM automatically. Enable signal export and map fields for each signal.
                    </span>
                    <a
                      className='zocks-text-primary text-decoration-underline'
                      href='https://help.zocks.io/en/articles/14811017-save-client-signals-to-your-crm'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Learn more
                    </a>
                  </p>
                </div>
                <div className='d-flex gap-3'>
                  <ScopedBlock
                    scope={{
                      scope: 'account:update',
                      superScope: ['account:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.REPLACE}
                    replaceElement={
                      <OverlayTrigger
                        trigger={['hover', 'focus']}
                        placement='top'
                        overlay={<BootstrapTooltip className='zocks-text-gray-500'>Your account admin can edit this setting.</BootstrapTooltip>}
                      >
                        <Form.Switch disabled={true} checked={account.effectiveConfig.businessEventExport} />
                      </OverlayTrigger>
                    }
                  >
                    <Form.Switch
                      onChange={handleBusinessEventExportChange}
                      disabled={isAccountUpdateLoading || !businessEventExportEnabled}
                      checked={account.effectiveConfig.businessEventExport}
                    />
                  </ScopedBlock>
                  <div className='d-flex flex-column gap-1'>
                    <span className='paragraph-medium'>Enable Client Signal export to CRM</span>
                    <span className='paragraph-small zocks-text-gray-700'>This affects all users in your account.</span>
                  </div>
                </div>
                {account.effectiveConfig.businessEventExport && (
                  <div className='d-flex flex-column align-items-start align-self-stretch gap-4'>
                    {enabledBusinessEventExportServices.length > 0 && (
                      <div className='d-flex flex-column align-items-start align-self-stretch gap-2'>
                        {enabledBusinessEventExportServices.map((serviceId) => (
                          <div
                            className='d-flex flex-column justify-content-center align-items-start align-self-stretch business-event-settings-integration-row-box'
                            key={serviceId}
                          >
                            <div className='d-flex align-items-start business-event-settings-integration-row-content gap-1'>
                              <div className='d-flex justify-content-center align-items-center flex-shrink-0 business-event-settings-integration-icon'>
                                <IntegrationIcon serviceId={serviceId} />
                              </div>
                              <div className='d-flex justify-content-between align-items-center align-self-stretch business-event-settings-integration-row-right'>
                                <div className='business-event-settings-integration-text'>{getIntegrationDisplayName(serviceId)}</div>
                                <div className='d-flex align-items-center gap-2 ms-auto'>
                                  <a className='business-event-settings-link' href={`${baseUrl}/account/integration-settings/integrations/${serviceId}/admin`}>
                                    Set up field mapping
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className='d-flex flex-column align-items-start align-self-stretch dont-see-crm-container'>
                      <div className='d-flex flex-column align-items-center align-self-stretch gap-1'>
                        <div className='business-event-settings-integration-text'>Don't see your CRM?</div>
                        <div className='d-flex align-items-center flex-column m-0'>
                          <div className='paragraph-small zocks-text-gray-900'>Client signal export isn't available for your CRM yet.</div>
                          <span className='d-flex flex-row gap-1'>
                            <div className='paragraph-small zocks-text-gray-900'>Let us know which integration you'd like — </div>
                            <a
                              className='zocks-text-primary paragraph-small text-decoration-underline'
                              href='mailto:support@zocks.io'
                              rel='noopener noreferrer'
                            >
                              support@zocks.io
                            </a>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
